import { DeleteIcon } from '@chakra-ui/icons';
import {
  ButtonGroup,
  IconButton,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { IconArrowRedoUp } from 'central-icons/IconArrowRedoUp';
import { IconArrowUndoUp } from 'central-icons/IconArrowUndoUp';
import mixpanel from 'mixpanel-browser';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetLoggedInUser } from '@revelio/auth';
import { BuildADashTrackingEvents, FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import { useEditMode } from '../EditModeProvider/EditModeProvider';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../PageConfigProvider/types';
import { usePageManager } from '../PageManagerProvider/PageManagerProvider';
import { EditParametersButton } from '../Reports/Parameters/EditParametersButton';
import { ClearPageDialog } from './ClearPageDialog';
import { DownloadMenuButton } from './DownloadMenuButton';
import { EditModeButtons } from './EditModeButtons';
import { SaveFilterSetButton } from './SaveFilterSetButton';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { UserPreferencesButton } from './UserPreferencesButton';
import { ViewModeButtons } from './ViewModeButtons';

export const PageActionBar = ({
  isReportPage = false,
}: {
  isReportPage?: boolean;
}) => {
  const { loggedInUser } = useGetLoggedInUser();
  const { editMode } = useEditMode();
  const {
    saveChanges,
    discardChanges,
    config,
    hasUnsavedChanges,
    undo,
    redo,
    canUndo,
    canRedo,
  } = usePageConfig();
  const { revelioPageId, dispatch: tabContentDispatch } = useTabContentConfig();

  const { templateId, action } = useParams<{
    templateId?: string;
    action?: string;
  }>();
  const isClone = action === 'clone';
  const isNewPage = !templateId && !isClone;

  const { id: pageId } = config;

  const toast = useToast();
  const navigate = useNavigate();
  const { editPage, clonePageDraft, viewPage } = usePageManager();

  const [isSaving, setIsSaving] = useState(false);

  const {
    isOpen: isDiscardDialogOpen,
    onOpen: onDiscardDialogOpen,
    onClose: onDiscardDialogClose,
  } = useDisclosure();
  const {
    isOpen: isClearPageDialogOpen,
    onOpen: onClearPageDialogOpen,
    onClose: onClearPageDialogClose,
  } = useDisclosure();
  // parameters modal state moved into EditParametersButton

  const isViewEditToggleEnabled = useUnleashFlag(
    FeatureFlag.CustomDashViewEditToggle
  );
  const customReportsIsEnabled = useUnleashFlag(FeatureFlag.CustomReports);
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const undoRedoIconSize = isNewDashboardLayout ? 14 : 16;
  const isOwner = !!config && loggedInUser?.id === config.owner.id;
  const hasContent = config?.tabs?.some((t) => t.tabContent.items.length > 0);

  const handleSetEditMode = () => {
    if (editMode || !pageId) return;
    editPage(pageId);
  };

  const handleSetViewMode = ({ isSaved = false }: { isSaved: boolean }) => {
    if (isClone && templateId && !isSaved) {
      viewPage(templateId, true);
      return;
    }

    if (!editMode || !pageId) return;
    viewPage(pageId);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isReportPage) {
        const validate = (): { isValid: boolean; message?: string } => {
          const tabs = config?.tabs ?? [];
          for (const tab of tabs) {
            const params = tab.tabContent?.reportParameters ?? [];
            if (params.length === 0) continue;
            const primaryCount = params.filter((p) => p.isPrimary).length;
            if (primaryCount !== 1) {
              return {
                isValid: false,
                message:
                  'Each tab with parameters must have exactly one primary parameter.',
              };
            }
          }
          return { isValid: true };
        };
        const { isValid, message } = validate();
        if (!isValid) {
          toast({
            position: 'top-right',
            title: 'Invalid parameter configuration',
            description: message ?? 'Please review report parameters.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          setIsSaving(false);
          return;
        }
      }
      if (await saveChanges()) {
        handleSetViewMode({ isSaved: true });
        const title = (() => {
          if (isClone) return 'Copy created';
          if (isNewPage) return 'Page created';
          return 'Changes saved';
        })();

        mixpanel.track(BuildADashTrackingEvents.SAVE_PAGE, {
          page_id: pageId,
        });
        toast({
          position: 'top-right',
          title,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
      // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
    } catch (error) {
      toast({
        position: 'top-right',
        title: 'Failed to save changes',
        description: 'Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmClearPage = () => {
    tabContentDispatch({ type: 'SET_ITEMS', payload: [] });
    onClearPageDialogClose();
  };

  const handleConfirmDiscard = () => {
    discardChanges();
    onDiscardDialogClose();
    !isNewPage &&
      toast({
        position: 'top-right',
        title: 'Your unsaved changes have been discarded.',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });

    const skipGuard = true;
    if (isNewPage) {
      navigate('/', { state: { skipGuard } });
    } else if (isClone && templateId) {
      viewPage(templateId, skipGuard);
    } else {
      viewPage(pageId, skipGuard);
    }
  };

  // auto-open now handled inside EditParametersButton

  return (
    <>
      <ButtonGroup
        size="sm"
        spacing={isNewDashboardLayout ? 0 : 2}
        alignItems="center"
      >
        {!editMode && (
          <>
            {!isNewDashboardLayout && !isReportPage && <SaveFilterSetButton />}
            <DownloadMenuButton />
            {!isReportPage && <UserPreferencesButton />}
            {isNewDashboardLayout && !isReportPage && <SaveFilterSetButton />}
            {isViewEditToggleEnabled &&
              (!isReportPage || customReportsIsEnabled) &&
              !isScreenerV2Page(revelioPageId) &&
              revelioPageId !== RevelioPageId.TALENT_DISCOVERY &&
              (isOwner ? (
                <ViewModeButtons
                  isOwner={isOwner}
                  onSetEditMode={handleSetEditMode}
                  pageId={pageId}
                />
              ) : (
                <ViewModeButtons
                  isOwner={isOwner}
                  onSetEditMode={() => pageId && clonePageDraft(pageId)}
                  pageId={pageId}
                />
              ))}
          </>
        )}

        {editMode && (
          <>
            {isReportPage && <EditParametersButton />}
            <IconButton
              aria-label="Clear page"
              icon={<DeleteIcon />}
              size="sm"
              rounded="full"
              onClick={onClearPageDialogOpen}
              {...(!isNewDashboardLayout && { colorScheme: 'navyBlue' })}
              variant={isNewDashboardLayout ? 'flat' : 'ghost'}
            />
            <IconButton
              aria-label="Undo"
              icon={<IconArrowUndoUp size={undoRedoIconSize} />}
              size="sm"
              rounded="full"
              onClick={undo}
              isDisabled={!canUndo}
              {...(!isNewDashboardLayout && { colorScheme: 'navyBlue' })}
              variant={isNewDashboardLayout ? 'flat' : 'ghost'}
            />
            <IconButton
              aria-label="Redo"
              icon={<IconArrowRedoUp size={undoRedoIconSize} />}
              size="sm"
              rounded="full"
              onClick={redo}
              isDisabled={!canRedo}
              {...(!isNewDashboardLayout && { colorScheme: 'navyBlue' })}
              variant={isNewDashboardLayout ? 'flat' : 'ghost'}
            />
            <EditModeButtons
              hasUnsavedChanges={hasUnsavedChanges}
              isSaving={isSaving}
              hasContent={hasContent}
              onSetViewMode={() => handleSetViewMode({ isSaved: false })}
              onDiscard={onDiscardDialogOpen}
              onSave={handleSave}
              isNewPage={isNewPage}
              isClone={isClone}
            />
          </>
        )}
      </ButtonGroup>

      <UnsavedChangesDialog
        isOpen={isDiscardDialogOpen}
        onClose={onDiscardDialogClose}
        onConfirm={handleConfirmDiscard}
        isNewPage={isNewPage}
      />
      <ClearPageDialog
        isOpen={isClearPageDialogOpen}
        onClose={onClearPageDialogClose}
        onConfirm={handleConfirmClearPage}
      />
    </>
  );
};
